import React, {CSSProperties, ReactNode, useState} from 'react';
import {Button, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import type {VoidCallback} from "@/@types/functions";
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import {If} from 'react-conditional-components-renderer';
import {isMobileView} from "@/lib/picos/browserFns";

export type ConfirmSeverity = 'normal' | 'info' | 'success' | 'warning' | 'error';

export type ConfirmPropsBase = {
    title: any;
    message?: string | ReactNode,
    buttonCaptionOk?: string;
    buttonCaptionCancel?: string;
    hideCancel?: boolean;
    noButtons?: boolean;
    onOk?: VoidCallback;
    onCancel?: VoidCallback;
    severity?: ConfirmSeverity;
    okColor?: 'success' | 'error' | 'primary' | 'warning' | 'info';
    styles?: CSSProperties;
    /** False keeps the dialog open on a click outside it, so only X, Escape or its own controls close it. */
    closeOnClickAway?: boolean;
}

export type ConfirmProps = React.PropsWithChildren<ConfirmPropsBase>;

function Confirm(props: ConfirmProps): ReactNode {
    const [open, setOpen] = useState(true);
    let {
        title,
        message,
        noButtons,
        hideCancel,
        buttonCaptionOk,
        buttonCaptionCancel,
        onCancel,
        onOk,
        severity,
        okColor,
    } = props;

    if (!title) title = 'Message';
    if (!buttonCaptionOk) buttonCaptionOk = 'Ok';
    if (!buttonCaptionCancel) buttonCaptionCancel = 'Cancel';
    if (!severity) severity = 'normal';

    const styles = props.styles ? {...props.styles} : {};

    // Always center!
    styles.margin = 'auto';
    styles.overflowY = 'visible';



    const _onCancel = () => {
        if (onCancel) onCancel();
        setOpen(false);
    }

    const _onOkButton: React.MouseEventHandler<HTMLAnchorElement> = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e?.preventDefault();
        e?.stopPropagation();

        if (onOk) onOk();
        setOpen(false);
    };

    const onClickAway = () => {
        if (props.closeOnClickAway === false) return;
        _onCancel();
    }

    // If a specific width is provided, disable the MUI preset maxWidth
    const hasCustomWidth = !!(styles.width || styles.maxWidth || styles.minWidth);

    return <ClickAwayListener onClickAway={onClickAway} mouseEvent='onMouseDown' touchEvent='onTouchStart'>
        <Dialog open={open}
                fullWidth={!!styles.maxWidth || hasCustomWidth}
                maxWidth={hasCustomWidth ? false : (isMobileView() ? 'sm' : 'xl')}
                sx={styles}
                onKeyDown={e => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        _onCancel();
                    }
                }}
        >
            <DialogTitle sx={{fontSize: '1rem', fontWeight: 'bold'}}>
                <DialogActions sx={{background: 'transparent', padding: '4px 0'}}>
                    {/* Show X button only when there's no Cancel button in the footer */}
                    {(noButtons || !onOk || !onCancel || hideCancel) &&
                        <div style={{marginRight: '.5em'}}>
                            <IconButton type={Icons.CLOSE} onClick={_onCancel}/>
                        </div>}

                    <div style={{marginRight: 'auto'}}>{title}</div>
                </DialogActions>

            </DialogTitle>

            <DialogContent sx={{overflowY: 'visible'}}>
                {/* Optional Message */}
                <If test={!!message}>
                    <DialogContentText>
                        {message}
                    </DialogContentText>
                </If>

                {/* Optional Content */}
                {props.children}
            </DialogContent>

            <If test={!noButtons && (!!onOk || !!onCancel)}>
                <DialogActions sx={{padding: '0.5em 1em 1em', background: 'white'}}>
                    {/* Cancel Button */}
                    {!hideCancel &&
                        <Button variant="text"
                                color="primary"
                                onClick={_onCancel}
                                size='small'
                        >
                            {buttonCaptionCancel}
                        </Button>}

                    {/* OK Button */}
                    <Button variant="text"
                            color={okColor || 'success'}
                            onClick={_onOkButton}
                            href={null}
                            size='small'
                    >
                        {buttonCaptionOk}
                    </Button>
                </DialogActions>
            </If>
        </Dialog>
    </ClickAwayListener>;
}

export default Confirm;
